import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { PortfolioIcon } from '@/components/ui/PortfolioIcon'

export const Header = () => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState('home')
  const [dark, setDark] = useState(false)
  const [themeReady, setThemeReady] = useState(false)
  const keys = [
    'home',
    'about',
    'projects',
    'services',
    'writing',
    'contact',
  ] as const
  const homeUrl = '/'
  const home = pathname === homeUrl

  useEffect(() => {
    try {
      setDark(localStorage.getItem('portfolio-theme') === 'dark')
    } catch {
      // The theme remains usable when browser storage is unavailable.
    }
    setThemeReady(true)
  }, [])

  useEffect(() => {
    if (!themeReady) return
    document.documentElement.dataset.theme = dark ? 'dark' : 'light'
    try {
      localStorage.setItem('portfolio-theme', dark ? 'dark' : 'light')
    } catch {
      // Persisting a preference is optional.
    }
  }, [dark, themeReady])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const section = entries.find((entry) => entry.isIntersecting)
        if (section) setActive(section.target.id)
      },
      { rootMargin: '-15% 0px -60% 0px' },
    )
    document
      .querySelectorAll('main > section[id]')
      .forEach((section) => observer.observe(section))
    return () => observer.disconnect()
  }, [pathname])

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [])

  return (
    <header className="site-header">
      <a className="skip-link" href="#main-content">
        {t('portfolio.nav.skip')}
      </a>
      <div className="shell header-inner">
        <a
          className="wordmark"
          href={home ? '#home' : homeUrl}
          aria-label="Marcos Aquino"
        >
          <span>
            MARCOS{' '}
            <b>
              AQUINO<span className="brand-dot">.</span>
            </b>
          </span>
          <small>{t('portfolio.brand')}</small>
        </a>
        <nav
          className={open ? 'site-nav is-open' : 'site-nav'}
          id="site-navigation"
          aria-label={t('portfolio.nav.label')}
        >
          {keys.map((key) => (
            <a
              key={key}
              href={`${home ? '' : homeUrl}#${key}`}
              className={
                (home && active === key) ||
                (!home && key === 'writing' && pathname.includes('/writing/'))
                  ? 'is-active'
                  : ''
              }
              aria-current={home && active === key ? 'location' : undefined}
              onClick={() => setOpen(false)}
            >
              {t(`portfolio.nav.${key}`)}
            </a>
          ))}
        </nav>
        <div className="header-tools">
          <button
            className="icon-button theme-switch"
            aria-label={t('portfolio.nav.theme')}
            aria-pressed={dark}
            onClick={() => setDark(!dark)}
          >
            <PortfolioIcon name={dark ? 'sun' : 'moon'} />
          </button>
          <button
            className="icon-button menu-toggle"
            aria-label={t(`portfolio.nav.${open ? 'close' : 'menu'}`)}
            aria-controls="site-navigation"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            <PortfolioIcon name={open ? 'close' : 'menu'} />
          </button>
        </div>
      </div>
    </header>
  )
}
